import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import usePageLoadAnimation from '../../hooks/usePageLoadAnimation';
import { useTheme } from '../../hooks/useTheme';
import logo from '../../resources/logo2.png';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const location = useLocation();
  const isLoaded = usePageLoadAnimation();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const products = [
    { name: 'SalesPro', path: '/products/salespro', icon: 'ri-line-chart-line', desc: 'Sales & Inventory Management' },
    { name: 'SchoolPetal', path: '/products/schoolpetal', icon: 'ri-school-line', desc: 'School Management System' },
    { name: 'BMS ERP', path: '/products/bmserp', icon: 'ri-building-line', desc: 'Business Management Suite' },
    { name: 'BraveIQ', path: '/products/braveiq', icon: 'ri-brain-line', desc: 'AI Exam Prep for Students' },
    { name: 'WordPro', path: '/products/wordpro', icon: 'ri-book-open-line', desc: 'Vocabulary & Spelling App' },
  ];

  const navLinks = [
    { name: 'Services', path: '/services' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-transparent backdrop-blur-lg shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link
              to="/"
              className={`flex items-center space-x-2 transition-all duration-300 ${
                isLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ transitionDelay: '300ms' }}
            >
              <img src={logo} alt="Livepetal" className="h-8 lg:h-10 w-auto rounded-lg" />
              <span className="text-[#0D1B4F] font-bold text-lg lg:text-xl font-['Syne']">
                Livepetal
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {/* Products Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setIsProductsOpen(true)}
                onMouseLeave={() => setIsProductsOpen(false)}
              >
                <button
                  className={`relative px-4 py-2 text-sm font-medium text-[#0D1B4F] hover:text-[#E8621A] transition-all duration-300 group flex items-center gap-1 ${
                    isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
                  } ${location.pathname.startsWith('/products') ? 'text-[#E8621A]' : ''}`}
                  style={{ transitionDelay: '350ms' }}
                >
                  Products
                  <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isProductsOpen ? 'rotate-180' : ''}`}></i>
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#E8621A] transition-all duration-300 group-hover:w-full"></span>
                </button>

                {/* Dropdown Menu */}
                <div
                  className={`absolute top-full left-0 pt-2 w-80 transition-all duration-300 ${
                    isProductsOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'
                  }`}
                >
                  <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
                    <div className="p-2">
                    {products.map((product, index) => (
                      <Link
                        key={product.path}
                        to={product.path}
                        className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors duration-200 group"
                      >
                        <div className="w-10 h-10 flex items-center justify-center bg-[#E8621A]/10 rounded-lg group-hover:bg-[#E8621A]/20 transition-colors duration-200 flex-shrink-0">
                          <i className={`${product.icon} text-[#E8621A] text-lg`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-[#0D1B4F] group-hover:text-[#E8621A] transition-colors duration-200">
                            {product.name}
                          </div>
                          <div className="text-xs text-gray-600 mt-0.5">
                            {product.desc}
                          </div>
                        </div>
                        <i className="ri-arrow-right-line text-gray-400 group-hover:text-[#E8621A] transition-colors duration-200 flex-shrink-0"></i>
                      </Link>
                    ))}
                    </div>
                    <div className="border-t border-gray-200 p-2">
                    <Link
                      to="/products"
                      className="flex items-center justify-center gap-2 p-3 text-sm font-semibold text-[#E8621A] hover:bg-[#E8621A]/5 rounded-xl transition-colors duration-200"
                    >
                      View All Products
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                    </div>
                  </div>
                </div>
              </div>

              {navLinks.map((link, index) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative px-4 py-2 text-sm font-medium text-[#0D1B4F] hover:text-[#E8621A] transition-all duration-300 group ${
                    isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
                  } ${location.pathname === link.path ? 'text-[#E8621A]' : ''}`}
                  style={{ transitionDelay: `${400 + index * 50}ms` }}
                >
                  {link.name}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#E8621A] transition-all duration-300 group-hover:w-full"></span>
                </Link>
              ))}
            </div>

            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center gap-3">
              {/* CTA Button */}
              <Link
                to="/contact"
                className={`inline-flex items-center px-6 py-2.5 bg-[#E8621A] text-white font-semibold rounded-lg hover:bg-[#FF8C42] hover:scale-105 hover:shadow-[0_0_30px_rgba(232,98,26,0.5)] transition-all duration-300 whitespace-nowrap ${
                  isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
                }`}
                style={{ transitionDelay: `${400 + (navLinks.length + 1) * 50}ms` }}
              >
                Get Started
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden w-10 h-10 flex flex-col items-center justify-center space-y-1.5 ${
                isMobileMenuOpen ? 'hamburger-open' : ''
              }`}
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              <span className="hamburger-line w-6 h-0.5 bg-[#0D1B4F] block"></span>
              <span className="hamburger-line w-6 h-0.5 bg-[#0D1B4F] block"></span>
              <span className="hamburger-line w-6 h-0.5 bg-[#0D1B4F] block"></span>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Drawer */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>

        {/* Drawer */}
        <div
          className={`absolute top-0 left-0 bottom-0 w-full max-w-sm bg-[#0F1526] shadow-2xl transition-transform duration-300 ease-out ${
            isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-[#0D1B4F]">
              <Link
                to="/"
                className="flex items-center space-x-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <img src={logo} alt="Livepetal" className="h-10 w-auto rounded-lg" />
                <span className="text-white font-bold text-xl font-['Syne']">Livepetal</span>
              </Link>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-10 h-10 flex items-center justify-center text-[#94A3B8] hover:text-white"
                aria-label="Close menu"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Navigation Links */}
            <div className="flex-1 overflow-y-auto py-6">
              <div className="flex flex-col space-y-1 px-4">
                {/* Products Section */}
                <div className="mb-2">
                  <div className="px-4 py-2 text-xs font-bold text-[#94A3B8] uppercase tracking-wider">
                    Products
                  </div>
                  {products.map((product) => (
                    <Link
                      key={product.path}
                      to={product.path}
                      className={`flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                        location.pathname === product.path
                          ? 'bg-[#E8621A]/10 text-[#E8621A]'
                          : 'text-[#94A3B8] hover:bg-[#0D1B4F] hover:text-white'
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <i className={`${product.icon} text-lg`}></i>
                      <span className="flex-1">{product.name}</span>
                      <i className="ri-arrow-right-s-line text-xl"></i>
                    </Link>
                  ))}
                </div>

                {/* Other Links */}
                <div className="border-t border-[#0D1B4F] pt-2 mt-2">
                  {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`flex items-center justify-between px-4 py-4 text-base font-medium rounded-lg transition-all duration-200 ${
                      location.pathname === link.path
                        ? 'bg-[#E8621A]/10 text-[#E8621A]'
                        : 'text-[#94A3B8] hover:bg-[#0D1B4F] hover:text-white'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span>{link.name}</span>
                    <i className="ri-arrow-right-s-line text-xl"></i>
                  </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="p-6 border-t border-[#0D1B4F] space-y-3">
              {/* CTA */}
              <Link
                to="/contact"
                className="flex items-center justify-center w-full px-6 py-4 bg-[#E8621A] text-white font-semibold rounded-lg hover:bg-[#FF8C42] hover:shadow-[0_0_30px_rgba(232,98,26,0.5)] transition-all duration-200 whitespace-nowrap"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Get Started
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
